<template>
  <div class="track-page-layout">
    <div v-if="isMobile" class="track-mobile-header">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <div
        v-if="track"
        class="dashboard-tab-bar"
        role="tablist"
        aria-label="Разделы трека"
      >
        <button
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': mobileTab === 'description' }"
          role="tab"
          :aria-selected="mobileTab === 'description'"
          @click="mobileTab = 'description'"
        >
          Описание
        </button>
        <button
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': mobileTab === 'map' }"
          role="tab"
          :aria-selected="mobileTab === 'map'"
          @click="mobileTab = 'map'"
        >
          Карта
        </button>
      </div>
    </div>

    <aside
      v-show="!isMobile || mobileTab === 'description'"
      class="track-sidebar"
      :class="{ 'track-sidebar--mobile-tab': isMobile }"
    >
      <div class="track-sidebar-scroll">
        <div v-if="loading" class="track-sidebar-content">
          <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />
          <h1 class="track-title">{{ track?.name || 'Трек' }}</h1>
          <p class="empty-state">Загрузка...</p>
        </div>

        <div v-else-if="error" class="track-sidebar-content">
          <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />
          <h1 class="track-title">{{ track?.name || 'Трек' }}</h1>
          <p class="error-message">{{ error }}</p>
        </div>

        <div v-else-if="track" class="track-sidebar-content">
        <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />
        <h1 class="track-title">{{ track.name }}</h1>
        <p v-if="isPlainDescription" class="track-description">{{ track.description }}</p>
        <div
          v-else-if="sanitizedDescription"
          class="track-description track-description--rich"
          v-html="sanitizedDescription"
        />
        <PhotoSlider
          v-if="track.photos?.length"
          ref="photoSliderRef"
          :photos="track.photos"
        />
        <p class="track-item-meta">
          {{ formatDistance(track.distance_m) }} · {{ formatDuration(track.duration_s) }}
        </p>
        <div v-if="track.difficulty_profiles?.length" class="track-difficulty">
          <p class="track-item-meta track-difficulty-title">Сложность</p>
          <ul class="track-difficulty-list">
            <li
              v-for="profile in track.difficulty_profiles"
              :key="profile.season"
              class="track-item-meta"
            >
              {{ seasonLabel(profile.season) }}: {{ formatDifficultyProfile(profile) }}
            </li>
          </ul>
        </div>
        <p v-if="!isOwner" class="track-item-meta">{{ visibilityLabel(track.public) }}</p>

        <fieldset v-if="isOwner" class="visibility-field">
          <legend class="track-item-meta">Видимость</legend>
          <label class="radio-label">
            <input
              type="radio"
              :checked="!track.public"
              :disabled="updatingVisibilitySettings"
              @change="toggleVisibility(false)"
            />
            Приватный
          </label>
          <label class="radio-label">
            <input
              type="radio"
              :checked="track.public"
              :disabled="updatingVisibilitySettings"
              @change="toggleVisibility(true)"
            />
            Публичный
          </label>
        </fieldset>

        <div v-if="track.author && !isOwner && auth.isAuthenticated" class="author-row">
          <AuthorLink :author="track.author" prefix="Автор:" size="sm" class="track-item-meta" />
          <FavoriteButton
            label="Автор в избранное"
            :favorited="authorFavorited"
            :disabled="favoritingAuthor"
            @favorite="favoriteAuthor"
            @unfavorite="unfavoriteAuthor"
          />
        </div>

        <div v-else-if="track.author" class="track-item-meta track-item-author">
          <AuthorLink :author="track.author" prefix="Автор:" size="sm" />
        </div>

        <div v-if="canDownload" class="track-download-row">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="downloadingGpx"
            @click="downloadCurrentTrackGpx"
          >
            {{ downloadingGpx ? 'Скачивание...' : 'Скачать GPX' }}
          </button>
        </div>

        <div v-if="auth.isAuthenticated" class="track-actions">
          <ReactionBar
            :likes-count="track.likes_count ?? 0"
            :dislikes-count="track.dislikes_count ?? 0"
            :user-reaction="track.user_reaction"
            :disabled="reactingTrack"
            @react="reactToTrack"
            @unreact="unreactToTrack"
          />
          <span class="track-stats">{{ track.comments_count ?? 0 }} 💬</span>
          <FavoriteButton
            label="Трек в избранное"
            :favorited="track.favorited"
            :disabled="favoritingTrack"
            @favorite="favoriteCurrentTrack"
            @unfavorite="unfavoriteCurrentTrack"
          />
        </div>

        <div v-else class="track-actions track-actions--readonly">
          <span>{{ track.likes_count ?? 0 }} 👍</span>
          <span>{{ track.dislikes_count ?? 0 }} 👎</span>
          <span>{{ track.comments_count ?? 0 }} 💬</span>
          <span>{{ track.completions_count ?? 0 }} ✓</span>
        </div>

        <GuestAuthPrompt v-if="!auth.isAuthenticated" />

        <TrackCompletionsSection
          :completions="completions"
          :completions-count="track.completions_count ?? 0"
          :can-create="auth.isAuthenticated"
          :can-delete="canDeleteCompletion"
          :deleting-id="deletingCompletionId"
          :submitting="submittingCompletion"
          :form-error="completionError"
          @create="submitCompletion"
          @delete="removeCompletion"
        />

        <div v-if="isOwner" class="btn-row" style="margin-top: 1rem">
          <router-link class="btn btn-secondary" :to="{ name: 'track-edit', params: { id: track.id } }">
            Редактировать
          </router-link>
          <button class="btn btn-danger" :disabled="deleting" @click="removeTrack">
            {{ deleting ? 'Удаление...' : 'Удалить' }}
          </button>
        </div>

        <section class="comments-section panel">
          <h2>Комментарии</h2>

          <label v-if="isOwner" class="comments-setting">
            <input
              type="checkbox"
              :checked="track.allow_comments"
              :disabled="updatingCommentsSettings"
              @change="toggleAllowComments"
            />
            Разрешить комментарии
          </label>

          <CommentList
            :comments="comments"
            :can-delete="canDeleteComment"
            :interactive="auth.isAuthenticated"
            :deleting-id="deletingCommentId"
            :reacting-id="reactingCommentId"
            delegate-gallery
            @delete="removeComment"
            @open-gallery="openGallery"
            @react="reactToComment"
            @unreact="unreactToComment"
          />

          <LoadMoreButton
            :has-more="hasMorePages(commentsPagination)"
            :loading="commentsLoadingMore"
            :disabled="commentsLoading"
            @click="loadMoreComments"
          />

          <CommentForm
            v-if="canComment"
            :pick-point-mode="pickPointMode"
            :picked-point="pickedPoint"
            :submitting="submittingComment"
            :error="commentError"
            @toggle-pick-point="togglePickPointMode"
            @clear-point="clearPickedPoint"
            @submit="submitComment"
          />
        </section>
        </div>
      </div>
    </aside>

    <div
      v-if="track"
      v-show="!isMobile || mobileTab === 'map'"
      class="track-map-wrap"
      :class="{ 'track-map-wrap--mobile-tab': isMobile }"
    >
      <button
        v-if="hasPhotoMarkers"
        type="button"
        class="btn map-photo-toggle"
        @click="showPhotoMarkers = !showPhotoMarkers"
      >
        {{ showPhotoMarkers ? 'Скрыть фото' : 'Фото на карте' }}
      </button>

      <TrackMap
        :tracks="mapTracks"
        :selected-track-id="track.id"
        :comments="comments"
        :track-photos="trackGeoPhotos"
        :show-photo-markers="showPhotoMarkers"
        :pick-point-mode="pickPointMode"
        :picked-point="pickedPoint"
        @pick-point="onPickPoint"
        @photo-marker-click="openGallery"
        @track-photo-click="openTrackPhotoGallery"
      />
    </div>

    <PhotoGallery
      :open="galleryOpen"
      :comment="galleryComment"
      :initial-photo-index="galleryPhotoIndex"
      @close="closeGallery"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DOMPurify from 'dompurify'
import AuthorLink from '../components/AuthorLink.vue'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import CommentForm from '../components/CommentForm.vue'
import CommentList from '../components/CommentList.vue'
import FavoriteButton from '../components/FavoriteButton.vue'
import GuestAuthPrompt from '../components/GuestAuthPrompt.vue'
import LoadMoreButton from '../components/LoadMoreButton.vue'
import PhotoGallery from '../components/PhotoGallery.vue'
import PhotoSlider from '../components/PhotoSlider.vue'
import ReactionBar from '../components/ReactionBar.vue'
import TrackCompletionsSection from '../components/TrackCompletionsSection.vue'
import TrackMap from '../components/TrackMap.vue'
import {
  createCompletion,
  deleteCompletion,
  fetchCompletions,
} from '../api/completions'
import {
  createComment,
  deleteComment,
  fetchComments,
  updateCommentsSettings,
} from '../api/comments'
import { favoriteTrack, favoriteUser, fetchFavoriteUsers, unfavoriteTrack, unfavoriteUser } from '../api/favorites'
import {
  removeCommentReaction,
  removeTrackReaction,
  setCommentReaction,
  setTrackReaction,
} from '../api/reactions'
import { deleteTrack, downloadTrackGpx, fetchTrack, updateVisibilitySettings } from '../api/tracks'
import { useMediaQuery } from '../composables/useMediaQuery'
import { useAuthStore } from '../stores/auth'
import { extractApiError } from '../utils/apiError'
import { downloadBlob } from '../utils/downloadBlob'
import {
  formatDifficultyProfile,
  formatDistance,
  formatDuration,
  seasonLabel,
  visibilityLabel,
} from '../utils/trackFormat'
import {
  isHtmlContent,
  RICH_DESCRIPTION_ALLOWED_ATTR,
  RICH_DESCRIPTION_ALLOWED_TAGS,
} from '../utils/richDescription'
import { trackMapColor } from '../utils/trackMapColors'
import { hasMorePages, parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const isMobile = useMediaQuery('(max-width: 767px)')
const mobileTab = ref('description')

const track = ref(null)
const comments = ref([])
const commentsPage = ref(1)
const commentsPagination = ref(null)
const commentsLoading = ref(false)
const commentsLoadingMore = ref(false)
const completions = ref([])
const loading = ref(true)
const error = ref('')
const deleting = ref(false)
const submittingComment = ref(false)
const submittingCompletion = ref(false)
const commentError = ref('')
const completionError = ref('')
const deletingCommentId = ref(null)
const deletingCompletionId = ref(null)
const reactingCommentId = ref(null)
const reactingTrack = ref(false)
const favoritingTrack = ref(false)
const favoritingAuthor = ref(false)
const authorFavorited = ref(false)
const updatingCommentsSettings = ref(false)
const updatingVisibilitySettings = ref(false)
const downloadingGpx = ref(false)
const pickPointMode = ref(false)
const pickedPoint = ref(null)
const showPhotoMarkers = ref(false)
const galleryOpen = ref(false)
const galleryComment = ref(null)
const galleryPhotoIndex = ref(0)
const photoSliderRef = ref(null)

const breadcrumbItems = computed(() => [
  { label: 'Треки', to: { name: 'home' } },
  { label: track.value?.name || 'Трек' },
])

const mapTracks = computed(() => {
  if (!track.value?.coordinates?.length) return []

  return [{
    id: track.value.id,
    name: track.value.name,
    coordinates: track.value.coordinates,
    color: trackMapColor(0),
  }]
})

const isOwner = computed(() => {
  if (!track.value || !auth.user) return false
  return track.value.user_id === auth.user.id
})

const canComment = computed(() => {
  if (!track.value || !auth.user) return false
  return isOwner.value || track.value.allow_comments
})

const canDownload = computed(() => {
  if (!track.value || !auth.isAuthenticated) return false
  return isOwner.value || track.value.public
})

const isPlainDescription = computed(() => {
  return track.value?.description && !isHtmlContent(track.value.description)
})

const sanitizedDescription = computed(() => {
  if (!track.value?.description || isPlainDescription.value) return ''

  return DOMPurify.sanitize(track.value.description, {
    ALLOWED_TAGS: RICH_DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_DESCRIPTION_ALLOWED_ATTR,
  })
})

const trackGeoPhotos = computed(() => {
  if (!track.value?.photos?.length) return []

  return track.value.photos
    .map((photo, index) => {
      if (photo.lat == null || photo.lng == null) return null
      return { lat: photo.lat, lng: photo.lng, src: photo.url, index }
    })
    .filter(Boolean)
})

const hasPhotoMarkers = computed(() => {
  const hasCommentPhotos = comments.value.some(
    (comment) => comment.lat != null && comment.lng != null && comment.photos?.length
  )

  return hasCommentPhotos || trackGeoPhotos.value.length > 0
})

function canDeleteComment(comment) {
  if (!track.value || !auth.user) return false
  return comment.author.id === auth.user.id || isOwner.value
}

function canDeleteCompletion(completion) {
  if (!track.value || !auth.user) return false
  return completion.user_id === auth.user.id || isOwner.value
}

async function loadTrack() {
  loading.value = true
  error.value = ''
  commentsPage.value = 1

  try {
    const { data } = await fetchTrack(route.params.id)
    track.value = data
    await Promise.all([loadComments(), loadCompletions(), loadAuthorFavoriteState()])
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить трек'
  } finally {
    loading.value = false
  }
}

async function loadAuthorFavoriteState() {
  if (!auth.isAuthenticated || !track.value || isOwner.value) {
    authorFavorited.value = false
    return
  }

  const { data } = await fetchFavoriteUsers()
  authorFavorited.value = data.some((author) => author.id === track.value.user_id)
}

async function loadComments({ reset = true } = {}) {
  if (!track.value) return

  if (reset) {
    commentsLoading.value = true
  } else {
    commentsLoadingMore.value = true
  }
  try {
    const response = await fetchComments(track.value.id, {
      page: commentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    comments.value = reset ? items : [...comments.value, ...items]
    commentsPagination.value = pagination
  } finally {
    commentsLoading.value = false
    commentsLoadingMore.value = false
  }
}

function loadMoreComments() {
  commentsPage.value += 1
  loadComments({ reset: false })
}

function updateCommentsPaginationTotal(total) {
  if (!commentsPagination.value) return

  const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE)
  commentsPagination.value = {
    ...commentsPagination.value,
    total,
    total_pages: totalPages,
  }
}

async function loadCompletions() {
  if (!track.value) return

  const { data } = await fetchCompletions(track.value.id)
  completions.value = data
}

async function removeTrack() {
  if (!track.value) return

  deleting.value = true
  try {
    await deleteTrack(track.value.id)
    router.push({ name: 'home' })
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить трек'
  } finally {
    deleting.value = false
  }
}

async function submitComment(formData, { reset }) {
  if (!track.value) return

  submittingComment.value = true
  commentError.value = ''

  try {
    const { data } = await createComment(track.value.id, formData)
    const newCount = (track.value.comments_count ?? 0) + 1
    track.value = {
      ...track.value,
      comments_count: newCount,
    }
    const lastPage = Math.max(1, Math.ceil(newCount / PAGE_SIZE))

    if (commentsPage.value >= lastPage) {
      comments.value = [...comments.value, data]
      updateCommentsPaginationTotal(newCount)
    } else {
      commentsPage.value = lastPage
      await loadComments({ reset: true })
    }
    reset()
    pickPointMode.value = false
    pickedPoint.value = null
  } catch (err) {
    const errors = err.response?.data?.errors
    commentError.value =
      errors?.join(', ') || err.response?.data?.error || 'Не удалось добавить комментарий'
  } finally {
    submittingComment.value = false
  }
}

async function removeComment(comment) {
  if (!track.value) return

  deletingCommentId.value = comment.id
  try {
    await deleteComment(track.value.id, comment.id)
    const newCount = Math.max((track.value.comments_count ?? 0) - 1, 0)
    track.value = {
      ...track.value,
      comments_count: newCount,
    }
    comments.value = comments.value.filter((item) => item.id !== comment.id)

    if (comments.value.length === 0 && commentsPage.value > 1) {
      commentsPage.value -= 1
      await loadComments({ reset: true })
    } else {
      updateCommentsPaginationTotal(newCount)
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить комментарий'
  } finally {
    deletingCommentId.value = null
  }
}

async function submitCompletion(payload) {
  if (!track.value) return

  submittingCompletion.value = true
  completionError.value = ''

  try {
    const { data } = await createCompletion(track.value.id, {
      completedAt: payload.completedAt,
      vehicleId: payload.vehicleId,
      vehicle: payload.vehicle,
      photos: payload.photos,
    })
    completions.value = [data, ...completions.value]
    track.value = {
      ...track.value,
      completions_count: (track.value.completions_count ?? 0) + 1,
    }
  } catch (err) {
    const errors = err.response?.data?.errors
    completionError.value =
      errors?.join(', ') || err.response?.data?.error || 'Не удалось отметить прохождение'
  } finally {
    submittingCompletion.value = false
  }
}

async function removeCompletion(completion) {
  if (!track.value) return

  deletingCompletionId.value = completion.id
  try {
    await deleteCompletion(track.value.id, completion.id)
    completions.value = completions.value.filter((item) => item.id !== completion.id)
    track.value = {
      ...track.value,
      completions_count: Math.max((track.value.completions_count ?? 0) - 1, 0),
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить прохождение'
  } finally {
    deletingCompletionId.value = null
  }
}

async function toggleAllowComments(event) {
  if (!track.value) return

  updatingCommentsSettings.value = true
  try {
    const { data } = await updateCommentsSettings(track.value.id, event.target.checked)
    track.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить настройки'
  } finally {
    updatingCommentsSettings.value = false
  }
}

async function toggleVisibility(isPublic) {
  if (!track.value || track.value.public === isPublic) return

  updatingVisibilitySettings.value = true
  try {
    const { data } = await updateVisibilitySettings(track.value.id, isPublic)
    track.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить видимость'
  } finally {
    updatingVisibilitySettings.value = false
  }
}

async function downloadCurrentTrackGpx() {
  if (!track.value || !canDownload.value) return

  downloadingGpx.value = true
  error.value = ''
  try {
    const response = await downloadTrackGpx(track.value.id)
    downloadBlob(response.data, gpxFilename())
  } catch (err) {
    error.value = extractApiError(err, 'Не удалось скачать GPX')
  } finally {
    downloadingGpx.value = false
  }
}

function gpxFilename() {
  const baseName = track.value?.name?.trim().replace(/[\\/:*?"<>|]+/g, '-').trim() || `track-${route.params.id}`
  return baseName.endsWith('.gpx') ? baseName : `${baseName}.gpx`
}

async function reactToTrack(value) {
  if (!track.value) return

  reactingTrack.value = true
  try {
    const { data } = await setTrackReaction(track.value.id, value)
    track.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingTrack.value = false
  }
}

async function unreactToTrack() {
  if (!track.value) return

  reactingTrack.value = true
  try {
    await removeTrackReaction(track.value.id)
    const { data } = await fetchTrack(track.value.id)
    track.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingTrack.value = false
  }
}

async function favoriteCurrentTrack() {
  if (!track.value) return

  favoritingTrack.value = true
  try {
    const { data } = await favoriteTrack(track.value.id)
    track.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить в избранное'
  } finally {
    favoritingTrack.value = false
  }
}

async function unfavoriteCurrentTrack() {
  if (!track.value) return

  favoritingTrack.value = true
  try {
    await unfavoriteTrack(track.value.id)
    track.value = { ...track.value, favorited: false }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать из избранного'
  } finally {
    favoritingTrack.value = false
  }
}

async function favoriteAuthor() {
  if (!track.value) return

  favoritingAuthor.value = true
  try {
    await favoriteUser(track.value.user_id)
    authorFavorited.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить автора в избранное'
  } finally {
    favoritingAuthor.value = false
  }
}

async function unfavoriteAuthor() {
  if (!track.value) return

  favoritingAuthor.value = true
  try {
    await unfavoriteUser(track.value.user_id)
    authorFavorited.value = false
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать автора из избранного'
  } finally {
    favoritingAuthor.value = false
  }
}

function updateCommentInList(updatedComment) {
  comments.value = comments.value.map((item) =>
    item.id === updatedComment.id ? updatedComment : item
  )
}

async function reactToComment(comment, value) {
  if (!track.value) return

  reactingCommentId.value = comment.id
  try {
    const { data } = await setCommentReaction(track.value.id, comment.id, value)
    updateCommentInList(data)
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

async function unreactToComment(comment) {
  if (!track.value) return

  reactingCommentId.value = comment.id
  try {
    await removeCommentReaction(track.value.id, comment.id)
    updateCommentInList({
      ...comment,
      user_reaction: null,
      likes_count:
        comment.user_reaction === 'like'
          ? Math.max((comment.likes_count ?? 0) - 1, 0)
          : comment.likes_count,
      dislikes_count:
        comment.user_reaction === 'dislike'
          ? Math.max((comment.dislikes_count ?? 0) - 1, 0)
          : comment.dislikes_count,
    })
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

function togglePickPointMode() {
  pickPointMode.value = !pickPointMode.value
  if (!pickPointMode.value) {
    pickedPoint.value = null
  }
}

function clearPickedPoint() {
  pickedPoint.value = null
}

function onPickPoint(point) {
  pickedPoint.value = point
  pickPointMode.value = false
}

function openGallery(comment, photoIndex = 0) {
  galleryComment.value = comment
  galleryPhotoIndex.value = photoIndex
  galleryOpen.value = true
}

function openTrackPhotoGallery({ index }) {
  photoSliderRef.value?.openAt(index)
}

function closeGallery() {
  galleryOpen.value = false
  galleryComment.value = null
  galleryPhotoIndex.value = 0
}

onMounted(loadTrack)
watch(() => route.params.id, loadTrack)
</script>

<style scoped>
.track-page-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.track-mobile-header {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 1.25rem 0;
}

.track-mobile-header .dashboard-tab-bar {
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.track-sidebar {
  width: 100%;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.track-sidebar--mobile-tab {
  flex: 1;
  min-height: 0;
  border-bottom: none;
}

.track-title {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
}

.track-sidebar-content {
  padding: 1rem 1.25rem;
}

.track-sidebar-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.track-description {
  margin-top: 0;
  white-space: pre-wrap;
}

.track-description--rich :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
}

.track-description--rich :deep(h2),
.track-description--rich :deep(h3) {
  margin: 1rem 0 0.5rem;
}

.track-difficulty {
  margin-top: 0.5rem;
}

.track-difficulty-title {
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.track-difficulty-list {
  margin: 0;
  padding-left: 1.1rem;
}

.track-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.track-download-row {
  margin-top: 1rem;
}

.track-stats {
  font-size: 0.875rem;
  color: #334155;
}

.visibility-field {
  border: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.visibility-field legend {
  margin-bottom: 0.375rem;
  padding: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.author-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.author-row .track-item-meta {
  margin-top: 0;
}

.comments-section {
  margin-top: 1.5rem;
}

.comments-section h2 {
  margin-top: 0;
}

.comments-setting {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.track-map-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.track-map-wrap--mobile-tab {
  flex: 1;
  min-height: 0;
}

@media (min-width: 768px) {
  .track-page-layout {
    flex-direction: row;
    height: 100%;
  }

  .track-sidebar {
    width: 360px;
    max-width: none;
    max-height: none;
    height: 100%;
    min-height: 0;
    border-bottom: none;
    border-right: 1px solid #e2e8f0;
  }
}

.map-photo-toggle {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}
</style>
